import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { ApiResponse } from '../../../../api/api-types/api-response.api';
import { Career, CareerMetadata, UpdateCareer } from '../../../../api/app-types/career.app';
import { ParticipationStatus } from '../../../../api/app-types/participation-status.app';
import { SocialSecurityStatusActivity } from '../../../../api/app-types/social-security-status.app';
import { CAREER_API } from '../../../../api/services/career.service';
import { mapEditFormFieldNamesToApiCareerFieldNames } from '../../../../api/transformers/career.transform';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSection } from '../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { matDateProviders, updateYearsOfDate } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LOGGER } from '../../../../shared/logging/log.service';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-career-edit',
	templateUrl: './career-edit.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatInputModule,
		MatSelectModule,
		LvrField,
		LvrInputDirective,
		LvrAppendAsteriskPipe,
		LvrDefaultActions,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrSection,
		LvrSectionForm,
		MatTooltip,
	],
	providers: [...matDateProviders],
})
export class CareerEdit extends LvrRedirectNav {
	public readonly StatusActivity = SocialSecurityStatusActivity;
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly logger = inject(LOGGER);
	private readonly api = inject(CAREER_API);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly clientId = this.resolveDataAsSignal<number>('clientId');
	private readonly careerData = this.resolveDataAsSignal<ApiResponse<Career, CareerMetadata> | undefined>('career');
	private readonly participationStatusPage =
		this.resolveDataAsSignal<PaginatedResponse<ParticipationStatus>>('participationStatus');
	public readonly participationStatuses = computed(() => {
		if (
			this.careerData()?.metadata.editOnlyFields.some(
				(field) => field === mapEditFormFieldNamesToApiCareerFieldNames('admissionId'),
			)
		)
			return (
				this.participationStatusPage()?.entities.filter((status) =>
					this.careerData()?.metadata.allowedAdmissionStatusTransitionsIds.includes(status.id),
				) || []
			);
		else return this.participationStatusPage()?.entities || [];
	});
	public readonly admissionId = this.resolveDataAsSignal<number>('admissionId');
	private readonly career = computed(() => this.careerData()?.data);

	private readonly env = inject(ENVIRONMENT);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly hasError = hasError;

	public readonly draftId = computed(() => this.career()?.id.toString() || '');
	public readonly canEditCareersPermission = hasPermission(this.session.permissions, 'Careers.Edit');
	public readonly isLoading = signal<boolean>(false);

	// Configuration for the min and max date for the date picker
	public readonly minMaxDate = {
		minYears: this.env.formConfiguration.career.startOfCareerRange.min,
		maxYears: this.env.formConfiguration.career.startOfCareerRange.max,
		min: updateYearsOfDate(-1 * this.env.formConfiguration.career.startOfCareerRange.min),
		max: updateYearsOfDate(this.env.formConfiguration.career.startOfCareerRange.max),
	};

	private readonly fb = inject(FormBuilder);
	public readonly form = this.fb.group({
		date: [
			this.career()?.startOfCareerStage ? new Date(this.career()!.startOfCareerStage) : null,
			[Validators.required, minDateValidator(this.minMaxDate.min), maxDateValidator(this.minMaxDate.max)],
		],
		remarkOfTheCareerStage: [this.career()?.remarkOfTheCareerStage ?? ''],
		admissionId: this.fb.control<number | undefined>(undefined, []),
	});

	public constructor() {
		super();
		effect(() => {
			const statuses = this.participationStatuses();
			const control = this.form.get('admissionId');
			const admissionId = this.admissionId();
			if (!control || !admissionId) return;

			if (control.disabled || statuses.some((status) => status.id === admissionId)) {
				control.setValue(admissionId);
			}
		});

		/**
		 * This effects reacts on the metadata coming from the resolver
		 * and enables/disables form controls based on the editOnlyFields array.
		 * If the array contains '*', all fields are editable.
		 */
		effect(() => {
			const meta = this.careerData()?.metadata?.editOnlyFields ?? [];
			if (meta.includes('*')) return;
			Object.keys(this.form.controls).forEach((key) => {
				const control = this.form.get(key);
				const metaValueOfControl = mapEditFormFieldNamesToApiCareerFieldNames(key);
				if (control) {
					const isEnabled = meta.some((field) => field.toLowerCase() === metaValueOfControl.toLowerCase());
					if (isEnabled) {
						control.enable();
					} else {
						control.disable();
					}
				}
			});

			// If admission is editable, keep only values that are selectable in current transitions.
			const admissionControl = this.form.get('admissionId');
			if (admissionControl?.enabled) {
				const selectableIds = this.participationStatuses().map((status) => status.id);
				if (!selectableIds.includes(admissionControl.value ?? -1)) {
					admissionControl.setValue(undefined, { emitEvent: false });
					admissionControl.updateValueAndValidity({ emitEvent: false });
				}
			}
		});
	}

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.isLoading.set(true);

		try {
			const data: UpdateCareer = {
				startOfCareerStage: this.form.value.date!,
				remarkOfTheCareerStage: this.form.value.remarkOfTheCareerStage?.trim(),
				admissionId: this.form.value.admissionId ?? undefined,
				id: this.career()!.id,
			};
			await this.api.update(this.clientId()!, data);
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.career_updated')),
			);
			await this.navigateAfterFinished(['../'], { relativeTo: this.route });
		} catch (e) {
			this.logger.error(e as Error);
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.career_update_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	public async cancel() {
		this.draftService.deleteDraft('career', this.draftId());
		await this.navigateAfterFinished(['../'], { relativeTo: this.route });
	}
}
